import { ItemId, TreeDestinationPosition, TreeItem } from '@atlaskit/tree';
import { AddIcon, Button, Checkbox, Dialog, Flex, ProviderConsumer, TrashCanIcon } from '@fluentui/react-northstar';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getNewGuid } from '../lifecycle/utils/helper';
import { Modal } from '../shared/components/Modal/Modal';
import draggableStyles from '../shared/components/tree/DraggableItem/DraggableItem.module.css';
import { CTreeItem, TreeData, TreeWrapper } from '../shared/components/tree/Tree';
import { ICustomProperty, ICustomPropertyListItem } from '../shared/interfaces/customproperties.interface';
import { platformService } from '../shared/services/platform.service';
import {
  ARCHIVE_CONFIRMATION_SUBTITLE,
  ARCHIVE_MODAL_HEADER,
  BUTTONS,
  DEFAULT_API_STATUS,
  DONE,
  TREE_LISTING_CONSTANTS,
  UNARCHIVE_CONFIRMATION_SUBTITLE,
  UNARCHIVE_MODAL_HEADER
} from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { toolbarButtonColor } from '../tikit/ticketHelper';

export interface CustomPropertyListItem {
  propertyListItems: ICustomPropertyListItem[];
  setPropertyListItems: React.Dispatch<React.SetStateAction<ICustomPropertyListItem[]>>;
  property: ICustomProperty;
}

export const CustomPropertyListItem = ({propertyListItems, setPropertyListItems, property}: CustomPropertyListItem) => {
  const api = new platformService();
  const {t} = useTranslation();
  const [dialog, setDialog] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [modalHeader, setModalHeader] = useState('');
  const [newOrEditDialog, setNewOrEditDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState('');
  const [deleteItem, setDeleteItem] = useState<MaybeNull<ICustomPropertyListItem>>(null);
  const archiveStorageKey = `isArchivedCustomPropertyListItems_${property?.Id ?? 0}View`;
  const [showArchived, setShowArchived] = useState(localStorage.getItem(archiveStorageKey) === "true");
  const [archiveItem, setArchiveItem] =
    useState<MaybeNull<CategoryDetail>>(null);
  const [enumList, setEnumList] = useState<TreeData>({
      rootId: '0',
      items: {
        '0': {
          id: '0',
          children: [],
          hasChildren: true,
          container: true,
          isExpanded: true,
          isChildrenLoading: false,
          data: {
            title: 'root'
          }
        }
      }
    });

  const defaultListItemDetail:ICustomPropertyListItem = {Id: null, Value: "", Position: 0, IsDefault: false, IsArchived: false, IsDeleted: false, ParentId: null};
  const [listItemDetail, setListItemDetail] = useState<ICustomPropertyListItem>(defaultListItemDetail);
  const [apiStatus, setAPIStatus] = useState<APIStatus>(
    DEFAULT_API_STATUS as APIStatus
  );

  const updateAPIStatus = (newState: Partial<APIStatus>) => {
    setAPIStatus({ ...apiStatus, ...newState });
  };

  const onDragHandler = async (
      source: ItemId,
      destination: TreeDestinationPosition,
      rootChildren
    ) => {
      let data: UpdateOnDrag = {
        Id: '',
        ParentId: '',
        Position: 0
      };
  
      const positionOfItem =
        enumList.items[destination.parentId.toString()].data.Position;
      let updatedRootChildren: UpdatePickerPositionPayload[] = [];
      updatedRootChildren = rootChildren.map((child, index) => ({
        '@odata.type': '#ServiceDesk.Core.Models.CustomProperties.CustomPropertyListItem',
        Id: child,
        Position: index + 1
      }));
  
      if (destination.parentId === '0') {
        data = {
          ...data,
          Id: source,
          ParentId: null
        };
      } else {
        data = {
          ...data,
          Id: source,
          ParentId: destination?.parentId,
          Position: positionOfItem
        };
      }

      let foundItems = _.cloneDeep(propertyListItems);
      foundItems = foundItems.map((p) => {
        if(p.Id.toString() == data.Id)
          p.ParentId = data.ParentId;
        const itemChange = updatedRootChildren.find(u => u.Id == p.Id);
        if(itemChange)
          p.Position = itemChange.Position;
        return p;
      });
      if (destination.parentId !== '0')
        foundItems = rearrangeParents(foundItems);
      setPropertyListItems(foundItems);
  };
  
  const rearrangeParents = (items: ICustomPropertyListItem[]) => {
    const parentItems = items.filter(p => p.ParentId == null).sort((a, b) => a.Position - b.Position).map((p, index) => {
      p.Position = index + 1;
      return p;
    });
    return items.map(p => {
      const parentItem = parentItems.find(pi => pi.Id == p.Id);
      if(parentItem)
        p.Position = parentItem.Position;
      return p;
    })
  };

  const sortTreeItems = (items: CTreeItem[]) =>
      Object.values(items).sort((a, b) => a.data.Position - b.data.Position);
  
  const checkSortedTreeExistItems = (
    items: CTreeItem[],
    rootChild: string[]
  ) => {
    const sortedArray: string[] = [];
    items.forEach(child => {
      if (rootChild.includes(child.id as string)) {
        sortedArray.push(child.id as string);
      }
    });
    return sortedArray;
  };

  const getTreeItems = useCallback((array, displayArchive) => {
      const items = array.reduce((acc, item) => {
        const child = item.IsArchived && !displayArchive ? [] : getChildsOfTree(array, item.Id, displayArchive);
        const sortedChild = child
          .sort((a, b) => a.position - b.position)
          .map(item => item.id.toString());
        return {
          ...acc,
          [item.Id.toString()]: {
            id: item.Id.toString(),
            children: [...sortedChild],
            hasChildren: child.length > 0,
            container: true,
            isExpanded: child.length > 0,
            isChildrenLoading: false,
            data: {
              title: item.Value,
              ...item
            }
          }
        };
      }, {} as CTreeItem);
      return items;
    }, []);
  
  const getChildsOfTree: (
    items: CTreeItem[],
    id: number | string,
    displayArchive: boolean
  ) => { id: number; position: number }[] = useCallback(
    (items: CTreeItem[], id: number | string, displayArchive: boolean) => {
      const child = [];
      
      for (let itemVal of items) {
        const element: CTreeItem['data'] = itemVal;
        if (element?.ParentId === id && (!element?.IsArchived || displayArchive )) {
          child.push({ id: element.Id.toString(), position: element.Position });
        }
      }
      return child;
    },
    []
  );

  const getRootChilds: (items: CTreeItem[]) => string[] = useCallback(
    (items: CTreeItem[]) => {
      const getChildOfRoot: string[] = [];

      Object.keys(items).forEach((key: string) => {
        getChildOfRoot.push(...items[key].children);
      });

      return Object.keys(items).filter(
        (val: string) => !getChildOfRoot.includes(val)
      );
    },
    []
  );

  const getData = (items, displayArchive) => {
    const propertyListItemsStringIds = items.filter(i => !i.IsDeleted).map(p => {
      p.Id = p.Id?.toString() ?? null;
      p.ParentId = p.ParentId?.toString() ?? null;
      return p;
    });
    const treeItems: CTreeItem[] = getTreeItems(propertyListItemsStringIds, displayArchive);
    const sortedArr = sortTreeItems(treeItems);
    const rootChilds = getRootChilds(treeItems);
    const sortedRootChilds = checkSortedTreeExistItems(sortedArr, rootChilds);

    setEnumList({
      ...enumList,
      // @ts-ignore
      items: {
        ...enumList.items,
        '0': {
          id: '0',
          children: [...sortedRootChilds],
          hasChildren: true,
          container: true,
          isExpanded: true,
          isChildrenLoading: false,
          data: {
            title: 'root'
          }
        },
        ...treeItems
      }
    });
  }

  useEffect(() => {
    if(propertyListItems){
      getData(propertyListItems, showArchived);
      updateAPIStatus({ status: DONE });
    }
  }, [propertyListItems])

  const closeModal = () => {
    setListItemDetail(defaultListItemDetail);
    setNewOrEditDialog(false);
  };

  const deleteData = () => {
    setDeleteItem(null);
    let items = _.cloneDeep(propertyListItems);
    items = items.map(p => {
      if(p.Id == deleteItem.Id)
        p.IsDeleted = true;
      return p;
    });
    if (deleteItem.Id !== 0) {
      api.deleteCustomPropertyListItem(deleteItem.Id);
    }
    setPropertyListItems(items);
    setOpenDelete(false);
  };

  const onArchiveSwitchChange = () => {
    const isArchived = localStorage.getItem(archiveStorageKey) === "true";
    localStorage.setItem(`isArchivedCustomPropertyListItems_${property?.Id ?? 0}View`, !isArchived ? "true" : "false");
    setShowArchived(!isArchived);
    getData(propertyListItems, !isArchived);
  }

  const archiveData = () => {
    setArchiveItem(null);
    let items = _.cloneDeep(propertyListItems);
    items = items.map(p => {
      if(p.Id == archiveItem.Id){
        p.IsArchived = !archiveItem.IsArchived;
      }
      return p;
    });
    if (archiveItem.Id !== 0) {
      api.updateCustomPropertyListItem({ ...archiveItem, IsArchived: !archiveItem.IsArchived });
    }

    setPropertyListItems(items);
    setDialog(false);
  };

  const hideArchivedDOM = (treeData: TreeItem) => {
    const item = treeData.data;
    return item.IsArchived && !showArchived;
  }

  const updateData = async(inputValue: string, checkBoxValue: boolean) => {
    let items = _.cloneDeep(propertyListItems);
    if(!listItemDetail.Id){
      const payload: ICustomPropertyListItem = {
        ...defaultListItemDetail,
        Value: inputValue,
        IsDefault: checkBoxValue,
        Id: getNewGuid(),
        Position: 1
      };
      items = items.map(p => {
        if(p.ParentId == null)
          p.Position = p.Position + 1;
        if(checkBoxValue)
          p.IsDefault = !checkBoxValue;
        return p;
      });
      items.unshift(payload);
    }else
      items = items.map(p => {
        if(checkBoxValue)
          p.IsDefault = !checkBoxValue;
        if(p.Id == listItemDetail.Id){
          p.IsDefault = checkBoxValue;
          p.Value = inputValue;
        }
        return p;
      });
    setPropertyListItems(items);
    setNewOrEditDialog(false);
  }

  const addMoreOptionsHandler = (item) => (
    <li
      className={draggableStyles.EditDropdownItem}
      onClick={() => {
        setDeleteItem(item.data); setOpenDelete(true);
      }}
    >
      <span className={draggableStyles.EditDropdownItemIcon}>
        <TrashCanIcon outline className={`${draggableStyles.svgIcon}`} />
      </span>
      <span className={draggableStyles.EditDropdownItemName}>
        {t('common.buttons.delete')}
      </span>
    </li>
  );

  return (
    <ProviderConsumer
    render={globalTheme => (
      <>
      <Flex styles={{justifyContent: 'space-between', marginTop: '1rem', marginBottom: '1rem'}}>
      <Button
        icon={<AddIcon />}
        text
        primary
        content={`${t('common.new')} ${t('custom-properties.list-item')}`}
        // disabled={!isUserLicensed}
        onClick={() => { setListItemDetail(defaultListItemDetail);setNewOrEditDialog(true); }}
        style={{
          color: toolbarButtonColor(true, globalTheme.siteVariables.colorScheme),
          height: '52px'
        }}
      />
      <Flex vAlign='center'>
        <Checkbox label={t('preferences.show-archived')} toggle onChange={onArchiveSwitchChange} checked={showArchived}/>
      </Flex>
      </Flex>
      <Flex>
      <TreeWrapper
        depth={Object.entries(enumList.items).length}
        data={enumList}
        isEditEnabled={true}
        onEditHandler={item => {
          setListItemDetail(item.data);
          setNewOrEditDialog(true);
        }}
        onArchiveHandler={item => {
          setArchiveItem(item.data);
          if (item.data.IsArchived) {
            setModalHeader(
              `${UNARCHIVE_MODAL_HEADER} ‘${item.data.title}’?`
            );
            setDialogContent(
              `${UNARCHIVE_CONFIRMATION_SUBTITLE} ${item.data.title} ${t('custom-properties.list-item')}?`
            );
          } else {
            setModalHeader(
              `${ARCHIVE_MODAL_HEADER} ‘${item.data.title}’?`
            );
            setDialogContent(
              `${ARCHIVE_CONFIRMATION_SUBTITLE} ${item.data.title} ${t('custom-properties.list-item')}?`
            );
          }
          setDialog(true);
        }}
        onDragEndHandler={onDragHandler}
        isDragEnabled={true}
        isLicensedUser={true}
        styles={{height: '10vh', overflow: 'unset'}}
        hideArchivedDOM={item => hideArchivedDOM(item)}
        addMoreOptions={item => addMoreOptionsHandler(item)}
      />
      </Flex>
      <Dialog cancelButton={t('common.buttons.cancel')} confirmButton={t('common.buttons.confirm')} 
        open={openDelete} onConfirm={deleteData} onCancel={() => { setOpenDelete(false); }} 
        header={t('custom-properties.confirm-delete-header', { name: deleteItem?.Value ?? "" })} 
        content={t('custom-properties.confirm-delete-sub', { name: deleteItem?.Value ?? "" })} style={{ maxWidth: '500px' }} />     
      <Dialog
        open={dialog}
        onCancel={() => setDialog(false)}
        cancelButton={toTitleCase(BUTTONS.CANCEL)}
        confirmButton={
          archiveItem?.IsArchived
            ? TREE_LISTING_CONSTANTS.unArchive
            : TREE_LISTING_CONSTANTS.archive
        }
        onConfirm={archiveData}
        content={dialogContent}
        header={modalHeader}
        style={{ maxWidth: '500px' }}
      />
      <Modal
        apiStatus={apiStatus}
        name={t('custom-properties.list-item')}
        open={newOrEditDialog}
        isUpdate={listItemDetail.Id?.toString().length > 0}
        inputValue={listItemDetail.Value}
        checkBoxValue={listItemDetail.IsDefault}
        onCancelHandler={closeModal}
        onConfirmationHandler={(inputValue, checkBoxValue) => {
          updateData(inputValue, checkBoxValue);
        }}
      />
  </>)} />
  );
};
